import { CountrySelector } from '@/components/common/CountrySelector';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { getLanguageOptions } from '@/constants/accountGeneralInfoOptions';
import { BillingAddressFormData } from '@/hooks/useBillingAddressForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../FormFieldWrapper';

// Swiss canton options (hardcoded with English labels for consistency)
const CANTON_OPTIONS = [
  { value: 'ZH', label: 'Zurich' },
  { value: 'BE', label: 'Bern' },
  { value: 'LU', label: 'Lucerne' },
  { value: 'UR', label: 'Uri' },
  { value: 'SZ', label: 'Schwyz' },
  { value: 'OW', label: 'Obwalden' },
  { value: 'NW', label: 'Nidwalden' },
  { value: 'GL', label: 'Glarus' },
  { value: 'ZG', label: 'Zug' },
  { value: 'FR', label: 'Fribourg' },
  { value: 'SO', label: 'Solothurn' },
  { value: 'BS', label: 'Basel-Stadt' },
  { value: 'BL', label: 'Basel-Landschaft' },
  { value: 'SH', label: 'Schaffhausen' },
  { value: 'AR', label: 'Appenzell Ausserrhoden' },
  { value: 'AI', label: 'Appenzell Innerrhoden' },
  { value: 'SG', label: 'St. Gallen' },
  { value: 'GR', label: 'Grisons' },
  { value: 'AG', label: 'Aargau' },
  { value: 'TG', label: 'Thurgau' },
  { value: 'TI', label: 'Ticino' },
  { value: 'VD', label: 'Vaud' },
  { value: 'VS', label: 'Valais' },
  { value: 'NE', label: 'Neuchâtel' },
  { value: 'GE', label: 'Geneva' },
  { value: 'JU', label: 'Jura' },
];

interface BillingAddressFormFieldsProps {
  control: Control<BillingAddressFormData>;
  isDifferentFromAccount: boolean;
  watchedBillingCountry?: string;
}

export function BillingAddressFormFields({
  control,
  isDifferentFromAccount,
  watchedBillingCountry,
}: BillingAddressFormFieldsProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-8'>
      {/* Header section with switch */}
      <div className='space-y-4'>
        <FormField
          control={control}
          name='isDifferentFromAccount'
          render={({ field, fieldState }) => (
            <div className='flex items-center space-x-3'>
              <Switch
                id='different-address'
                checked={field.value}
                onCheckedChange={field.onChange}
                className='data-[state=checked]:bg-primary'
              />
              <FormFieldWrapper
                label={tForms('billing.differentAddressToggle')}
                name='isDifferentFromAccount'
                tooltip={tForms('billing.tooltips.differentAddress')}
                error={fieldState.error?.message}
                className='!space-y-0'
              >
                <div className='text-xs text-muted-foreground mt-1'>
                  {tForms('billing.differentAddressDescription')}
                </div>
              </FormFieldWrapper>
            </div>
          )}
        />
      </div>

      {/* Conditional address fields */}
      {isDifferentFromAccount && (
        <div className='space-y-6 border-t pt-6'>
          <h3 className='text-lg font-semibold text-foreground'>
            {tForms('billing.addressInformation')}
          </h3>

          {/* Row 1: Name & Country */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingName'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingName')}
                  name='billingName'
                  required
                  tooltip={tForms('billing.tooltips.name')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingName'
                    type='text'
                    placeholder={tForms('placeholders.fullName')}
                    className='w-full min-h-[44px]'
                    autoComplete='name'
                  />
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='billingCountry'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingCountry')}
                  name='billingCountry'
                  required
                  tooltip={tForms('billing.tooltips.country')}
                  error={fieldState.error?.message}
                >
                  <CountrySelector
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={tForms('placeholders.selectCountry')}
                    error={fieldState.error?.message}
                    required
                    name='billingCountry'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Row 2: Street & House Number */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingStreet'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingStreet')}
                  name='billingStreet'
                  required
                  tooltip={tForms('billing.tooltips.street')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingStreet'
                    type='text'
                    placeholder={tForms('placeholders.streetName')}
                    className='w-full min-h-[44px]'
                    autoComplete='address-line1'
                  />
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='billingHouseNumber'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingStreetNumber')}
                  name='billingHouseNumber'
                  required
                  tooltip={tForms('billing.tooltips.houseNumber')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingHouseNumber'
                    type='text'
                    placeholder={tForms('placeholders.streetNumber')}
                    className='w-full min-h-[44px]'
                    autoComplete='address-line2'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Row 3: Post Box (Optional) */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingPostBox'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingPostBox')}
                  name='billingPostBox'
                  tooltip={tForms('billing.tooltips.postBox')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingPostBox'
                    type='text'
                    placeholder={tForms('placeholders.billingPostBox')}
                    className='w-full min-h-[44px]'
                  />
                </FormFieldWrapper>
              )}
            />
            <div></div> {/* Empty column for spacing */}
          </div>

          {/* Row 4: Postal Code & City */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingPostalCode'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingZipCode')}
                  name='billingPostalCode'
                  required
                  tooltip={tForms('billing.tooltips.postalCode')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingPostalCode'
                    type='text'
                    placeholder={tForms('placeholders.enterZipCode')}
                    className='w-full min-h-[44px]'
                    autoComplete='postal-code'
                    pattern='[0-9]*'
                    inputMode='numeric'
                  />
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='billingCity'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingCity')}
                  name='billingCity'
                  required
                  tooltip={tForms('billing.tooltips.city')}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id='billingCity'
                    type='text'
                    placeholder={tForms('placeholders.cityName')}
                    className='w-full min-h-[44px]'
                    autoComplete='address-level2'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Row 5: Canton */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingCanton'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingCanton')}
                  name='billingCanton'
                  required={watchedBillingCountry === 'CH'}
                  tooltip={tForms('tooltips.canton')}
                  error={fieldState.error?.message}
                >
                  <Select
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    disabled={watchedBillingCountry !== 'CH'}
                  >
                    <SelectTrigger className='min-h-[44px] w-full'>
                      <SelectValue
                        placeholder={
                          watchedBillingCountry === 'CH'
                            ? tForms('placeholders.selectOption')
                            : tForms('placeholders.selectCountryFirst')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {watchedBillingCountry === 'CH' ? (
                        CANTON_OPTIONS.map(option => (
                          <SelectItem
                            key={option.value}
                            value={String(option.value)}
                          >
                            {option.label}
                          </SelectItem>
                        ))
                      ) : (
                        <div className='p-2 text-sm text-muted-foreground'>
                          {tForms('placeholders.selectCountryFirst')}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </FormFieldWrapper>
              )}
            />
            <div></div> {/* Empty space for alignment */}
          </div>

          {/* Row 6: Language & State/Region */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='billingLanguage'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('fields.billingLanguage')}
                  name='billingLanguage'
                  required
                  tooltip={tForms('billing.tooltips.language')}
                  error={fieldState.error?.message}
                >
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className='w-full min-h-[44px]'>
                      <SelectValue
                        placeholder={tForms('placeholders.billingLanguage')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getLanguageOptions(tForms).map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormFieldWrapper>
              )}
            />
          </div>
        </div>
      )}

      {/* Email fields - Always visible */}
      <div className='space-y-6 border-t pt-6'>
        <h3 className='text-lg font-semibold text-foreground'>
          {tForms('billing.emailAddresses')}
        </h3>

        {/* Row 1: Email for invoices & reminders and Email for coverage & money orders */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
          <FormField
            control={control}
            name='emailInvoicesReminders'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('fields.emailInvoicesReminders')}
                name='emailInvoicesReminders'
                required
                tooltip={tForms('billing.tooltips.emailInvoicesReminders')}
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id='emailInvoicesReminders'
                  type='email'
                  placeholder={tForms('placeholders.emailInvoicesReminders')}
                  className='w-full min-h-[44px]'
                  autoComplete='email'
                />
              </FormFieldWrapper>
            )}
          />

          <FormField
            control={control}
            name='emailCoverageMoneyOrders'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('fields.emailCoverageMoneyOrders')}
                name='emailCoverageMoneyOrders'
                required
                tooltip={tForms('billing.tooltips.emailCoverageMoneyOrders')}
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id='emailCoverageMoneyOrders'
                  type='email'
                  placeholder={tForms('placeholders.emailCoverageMoneyOrders')}
                  className='w-full min-h-[44px]'
                  autoComplete='email'
                />
              </FormFieldWrapper>
            )}
          />
        </div>

        {/* Row 2: Email for reporting */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
          <FormField
            control={control}
            name='emailReporting'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('fields.emailReporting')}
                name='emailReporting'
                required
                tooltip={tForms('billing.tooltips.emailReporting')}
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id='emailReporting'
                  type='email'
                  placeholder={tForms('placeholders.emailReporting')}
                  className='w-full min-h-[44px]'
                  autoComplete='email'
                />
              </FormFieldWrapper>
            )}
          />
          <div></div> {/* Empty column for spacing */}
        </div>
      </div>
    </div>
  );
}
